
import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useGoogleAuth } from "@/contexts/GoogleAuthContext";

interface BusinessInfoFormProps {
  businessInfo: string;
  onBusinessInfoChange: (value: string) => void;
  onSave?: () => Promise<void>;
  isSaving?: boolean;
}

const BusinessInfoForm = ({ 
  businessInfo, 
  onBusinessInfoChange, 
  onSave, 
  isSaving = false 
}: BusinessInfoFormProps) => {
  const { isAuthenticated, supabaseSession } = useGoogleAuth();
  const [localIsSaving, setLocalIsSaving] = useState(false);

  const handleSave = async () => {
    try {
      setLocalIsSaving(true);
      
      if (onSave) {
        // Use provided onSave function for database saving
        await onSave();
      } else if (isAuthenticated && supabaseSession?.user?.id) {
        // Save to Supabase directly if authenticated
        const userId = supabaseSession.user.id;
        
        // First check if record exists
        const { data: existingRecord } = await supabase
          .from("ai_context")
          .select("id")
          .eq("user_id", userId)
          .maybeSingle();
          
        if (existingRecord) {
          // Update existing record
          await supabase
            .from("ai_context")
            .update({
              business_info: businessInfo,
              updated_at: new Date().toISOString()
            })
            .eq("id", existingRecord.id);
            
          toast.success("Business information saved to database");
        } else {
          // Insert new record
          await supabase
            .from("ai_context")
            .insert([{
              user_id: userId,
              business_info: businessInfo
            }]);
            
          toast.success("Business information saved to database");
        }
      } else {
        toast.error("Unable to save business information. Please log in.");
      }
    } catch (error) {
      console.error("Error saving business info:", error);
      toast.error("Failed to save business information");
    } finally {
      setLocalIsSaving(false);
    }
  };
  
  const exampleText = `Company Name: Acme AI
Industry: Machine Learning / Healthcare
Stage: Series A
Traction: $2M ARR, growing 15% MoM, 50+ enterprise customers
Revenue: $500K last year, $15M in current pipeline for this year
Key Differentiation: Proprietary algorithm for medical image analysis with 99.7% accuracy
Target Fundraise: $8M to expand sales team and enter European market
Current Investors: XYZ Ventures, Angel Investor Jane Doe`;

  return (
    <div className="space-y-4">
      <Alert className="bg-blue-50 border-blue-200">
        <div className="flex items-start">
          <Info className="h-4 w-4 mt-1 text-blue-600 mr-2" />
          <AlertDescription className="text-blue-700 text-sm">
            Make sure to include specific traction metrics (revenue, growth rates, pipeline) - this information will be highlighted in the generated emails.
          </AlertDescription>
        </div>
      </Alert>
      
      <div>
        <Textarea
          placeholder="Describe your business, traction, key metrics, fundraising goals, etc."
          className="min-h-[200px]"
          value={businessInfo}
          onChange={(e) => onBusinessInfoChange(e.target.value)}
        />
        
        {!businessInfo && (
          <div className="mt-2">
            <p className="text-sm text-gray-500">Example:</p>
            <pre className="text-xs bg-gray-50 p-3 rounded border mt-1 whitespace-pre-wrap">
              {exampleText}
            </pre>
          </div>
        )}
      </div>
      
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={!businessInfo.trim() || isSaving || localIsSaving}
          className="bg-brand-600 hover:bg-brand-700"
        >
          {(isSaving || localIsSaving) ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Business Info'
          )}
        </Button>
      </div>
    </div>
  );
};

export default BusinessInfoForm;
